import { useAuthenticatedAxiosInstance } from './useAuthenticatedAxiosInstance';
import { useCallback } from 'react';
import { getEnvironmentConfig } from '../environmentConfig';

/**
 * One authorized MCP agent (OAuth client). Mirrors `AgentGrantSummary` from the
 * API's `agent.service.ts`. `allowedOrgIds` are the orgs the user has granted
 * this agent; personal-scope access is governed by `personalAllowed`.
 */
export interface AuthorizedAgent {
    id: string;
    clientId: string;
    clientName: string | null;
    allowedOrgIds: string[];
    /** Whether the agent may reach the user's personal (no-org) scope. Default true. */
    personalAllowed: boolean;
    lastSeenAt: string;
}

export const useAgentsApi = () => {
    const api = useAuthenticatedAxiosInstance(`${getEnvironmentConfig().graphServiceUrl}`);

    const listAgents = useCallback(async (): Promise<AuthorizedAgent[]> => {
        const result = await api.get('/agents');
        return result.data.agents;
    }, [api]);

    const revokeAgent = useCallback(
        async (id: string): Promise<void> => {
            await api.delete(`/agents/${id}`);
        },
        [api],
    );

    const grantAgentOrg = useCallback(
        async (id: string, orgId: string): Promise<void> => {
            await api.post(`/agents/${id}/orgs`, { orgId });
        },
        [api],
    );

    const ungrantAgentOrg = useCallback(
        async (id: string, orgId: string): Promise<void> => {
            await api.delete(`/agents/${id}/orgs/${orgId}`);
        },
        [api],
    );

    const setAgentPersonal = useCallback(
        async (id: string, allowed: boolean): Promise<void> => {
            await api.put(`/agents/${id}/personal`, { allowed });
        },
        [api],
    );

    return { listAgents, revokeAgent, grantAgentOrg, ungrantAgentOrg, setAgentPersonal };
};
